import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@material-ui/core';
import { useEffect, useState } from 'react';

const SQUADS_CATALOG_TYPE_ID = '01HKXGT98HRHBTK3G1AXCWJQT4';

type TeamOption = {
  id: string;
  name: string;
};

export const IncidentIOTeamWithoutSchedulePicker = (props: any) => {
  const {
    onChange,
    rawErrors,
    required,
    formData,
    schema,
    idSchema,
    uiSchema,
  } = props;

  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchTeamsWithoutSchedules() {
      try {
        const proxyUrl = await discoveryApi.getBaseUrl('proxy');

        // Fetch all squads (paginated)
        const allSquads: TeamOption[] = [];
        let after: string | undefined;

        do {
          const params = new URLSearchParams({
            catalog_type_id: SQUADS_CATALOG_TYPE_ID,
            page_size: '250',
          });
          if (after) {
            params.set('after', after);
          }

          const squadsResponse = await fetchApi.fetch(
            `${proxyUrl}/incident-io/v3/catalog_entries?${params.toString()}`,
          );
          if (!squadsResponse.ok) {
            throw new Error('Failed to fetch squads from incident.io');
          }

          const squadsData = await squadsResponse.json();
          const entries = squadsData.catalog_entries || [];
          for (const entry of entries) {
            allSquads.push({ id: entry.id, name: entry.name });
          }

          after = squadsData.pagination_meta?.after;
        } while (after);

        // Fetch all schedules (paginated)
        const teamIdsWithSchedule = new Set<string>();
        let page = 1;
        let hasMore = true;

        while (hasMore) {
          const schedulesResponse = await fetchApi.fetch(
            `${proxyUrl}/incident-io/v2/schedules?page_size=100&page=${page}`,
          );
          if (!schedulesResponse.ok) {
            throw new Error('Failed to fetch schedules from incident.io');
          }

          const schedulesData = await schedulesResponse.json();
          const schedules = schedulesData.schedules || [];

          for (const schedule of schedules) {
            if (Array.isArray(schedule.team_ids)) {
              for (const teamId of schedule.team_ids) {
                teamIdsWithSchedule.add(teamId);
              }
            }
          }

          hasMore =
            schedulesData.pagination_meta?.after !== null ||
            schedules.length === 100;
          page++;

          if (schedules.length < 100) {
            hasMore = false;
          }
        }

        if (cancelled) return;

        const teamsWithoutSchedule = allSquads
          .filter(squad => !teamIdsWithSchedule.has(squad.id))
          .sort((a, b) => a.name.localeCompare(b.name));

        setTeams(teamsWithoutSchedule);
        setLoading(false);
      } catch (err: any) {
        if (cancelled) return;
        setError(err.message || 'Failed to load teams');
        setLoading(false);
      }
    }

    fetchTeamsWithoutSchedules();

    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi]);

  const title = uiSchema?.['ui:title'] || schema?.title || 'Team';
  const description =
    uiSchema?.['ui:description'] ||
    schema?.description ||
    'Select a team that does not yet have a schedule configured.';

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
      fullWidth
    >
      <InputLabel id={`${idSchema?.$id}-label`}>{title}</InputLabel>
      <Select
        labelId={`${idSchema?.$id}-label`}
        id={idSchema?.$id}
        value={formData ?? ''}
        onChange={e => onChange(e.target.value as string)}
        disabled={loading}
        displayEmpty={loading}
      >
        {loading && (
          <MenuItem value="" disabled>
            Loading teams...
          </MenuItem>
        )}
        {error && (
          <MenuItem value="" disabled>
            Error: {error}
          </MenuItem>
        )}
        {!loading && !error && teams.length === 0 && (
          <MenuItem value="" disabled>
            All teams already have schedules
          </MenuItem>
        )}
        {teams.map(team => (
          <MenuItem key={team.id} value={team.id}>
            {team.name}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{description}</FormHelperText>
    </FormControl>
  );
};
